import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, X } from "lucide-react";

interface Merchant {
  id: string;
}

interface FunnelFiltersProps {
  merchantId: string;
  setMerchantId: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  leadDirection: "inbound" | "inbound-email" | "outbound" | "pauta" | undefined;
  setLeadDirection: (
    value: "inbound" | "inbound-email" | "outbound" | "pauta" | undefined
  ) => void;
  projectId: string;
  setProjectId: (value: string) => void;
  merchants: Merchant[] | undefined;
  onApplyFilters: () => void;
  isLoading: boolean;
}

const FunnelFilters = ({
  merchantId,
  setMerchantId,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  leadDirection,
  setLeadDirection,
  projectId,
  setProjectId,
  merchants,
  onApplyFilters,
  isLoading,
}: FunnelFiltersProps) => {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div className="space-y-2">
            <Label htmlFor="merchant">Merchant</Label>
            <Select
              value={merchantId || "all"}
              onValueChange={(value) => setMerchantId(value === "all" ? "" : value)}
            >
              <SelectTrigger id="merchant">
                <SelectValue placeholder="All merchants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All merchants</SelectItem>
                {merchants?.map((merchant) => (
                  <SelectItem key={merchant.id} value={merchant.id}>
                    {merchant.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="startDate">Start Date</Label>
            <Input 
              id="startDate"
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)} 
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="endDate">End Date</Label>
            <Input 
              id="endDate"
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)} 
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="leadDirection">Lead Direction</Label>
            <Select
              value={leadDirection || "all"}
              onValueChange={(value) =>
                setLeadDirection(
                  value === "all"
                    ? undefined
                    : (value as "inbound" | "inbound-email" | "outbound" | "pauta")
                )
              }
            >
              <SelectTrigger id="leadDirection">
                <SelectValue placeholder="All directions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All directions</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="inbound-email">Inbound Email</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
                <SelectItem value="pauta">Pauta</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="projectId">Project ID</Label>
            <div className="relative">
              <Input
                id="projectId"
                placeholder="Optional"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className={projectId ? "pr-8" : ""}
              />
              {projectId && (
                <button
                  type="button"
                  onClick={() => setProjectId("")}
                  aria-label="Clear project ID"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-end">
            <Button
              onClick={onApplyFilters}
              disabled={!startDate || !endDate || isLoading}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FunnelFilters;
